import type { DocumentSearchSourceMetadata } from '@newfront-insurance/ai-api';
import type { UIMessagePart } from 'ai';

import type { UIMessageWithMetadata } from './types';

/**
 * Provider metadata namespace for document search sources
 */
const PROVIDER_METADATA_NAMESPACE = 'newfrontDocumentSearch' as const;

/**
 * Represents a source URL part from the AI SDK stream.
 *
 * Note: AI SDK expects providerMetadata to be Record<string, Record<string, JSONValue>>.
 * Our backend wraps our metadata under the "newfrontDocumentSearch" key.
 */
export type SourceUrlPart = {
  type: 'source-url';
  sourceId: string;
  url: string;
  title: string;
  providerMetadata?: {
    [PROVIDER_METADATA_NAMESPACE]?: DocumentSearchSourceMetadata;
  };
};

/**
 * Map of sourceId to source data
 */
export type SourcesMap = Map<string, SourceUrlPart>;

/**
 * Type guard to check if a message part is a source-url part
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function isSourceUrlPart(part: UIMessagePart<any, any>): part is SourceUrlPart {
  return part.type === 'source-url' && 'sourceId' in part;
}

/**
 * Helper to safely get document search metadata from a source
 */
export function getSourceMetadata(source: SourceUrlPart): DocumentSearchSourceMetadata | undefined {
  return source.providerMetadata?.[PROVIDER_METADATA_NAMESPACE];
}

/**
 * Pattern to match [SOURCE-<id>] citations in text
 */
const CITATION_PATTERN = /\[SOURCE-([^\]]+)\]/g;

/**
 * Extracts cited source IDs from message text parts.
 * Returns a Set of sourceIds that are referenced via [SOURCE-xxx] markers.
 */
function extractCitedSourceIds(message: UIMessageWithMetadata): Set<string> {
  const citedSourceIds = new Set<string>();

  if (!message.parts) {
    return citedSourceIds;
  }

  message.parts.forEach((part) => {
    if (part.type === 'text') {
      const { text } = part;
      const matches = Array.from(text.matchAll(CITATION_PATTERN));
      matches.forEach((match) => {
        const sourceId = match[1];
        citedSourceIds.add(sourceId);
      });
    }
  });

  return citedSourceIds;
}

/**
 * Extracts source-url parts from a message and builds a sources map.
 * Filtering rules:
 * - Sources WITH newfrontDocumentSearch metadata: only included if cited in text
 * - Sources WITHOUT newfrontDocumentSearch metadata: always included
 * The map preserves insertion order, which determines citation numbering.
 */
export function extractSourcesFromMessage(message: UIMessageWithMetadata): SourcesMap {
  const sourcesMap = new Map<string, SourceUrlPart>();

  if (!message.parts) {
    return sourcesMap;
  }

  // First, find all cited source IDs in the message text
  const citedSourceIds = extractCitedSourceIds(message);

  // Then, collect sources based on their metadata type
  message.parts.forEach((part) => {
    if (isSourceUrlPart(part)) {
      const hasDocumentSearchMetadata = !!getSourceMetadata(part);

      if (hasDocumentSearchMetadata) {
        // Only include document search sources if they're cited in the text
        if (citedSourceIds.has(part.sourceId)) {
          sourcesMap.set(part.sourceId, part);
        }
      } else {
        // Always include sources from other namespaces
        sourcesMap.set(part.sourceId, part);
      }
    }
  });

  return sourcesMap;
}

/**
 * Checks if a message has any sources that should be displayed.
 * Returns true if there are:
 * - Sources with newfrontDocumentSearch metadata that are cited in text, OR
 * - Sources without newfrontDocumentSearch metadata (always shown)
 * This matches the filtering logic in extractSourcesFromMessage.
 */
export function hasSources(message: UIMessageWithMetadata): boolean {
  if (!message.parts) {
    return false;
  }

  const citedSourceIds = extractCitedSourceIds(message);

  return message.parts.some((part) => {
    if (isSourceUrlPart(part)) {
      const hasDocumentSearchMetadata = !!getSourceMetadata(part);

      if (hasDocumentSearchMetadata) {
        // Document search sources must be cited
        return citedSourceIds.has(part.sourceId);
      }
      // Other sources are always shown
      return true;
    }
    return false;
  });
}

/**
 * Gets an array of sources in insertion order.
 * The array index determines the citation number (1-based).
 */
export function getSourcesArray(sourcesMap: SourcesMap): SourceUrlPart[] {
  return Array.from(sourcesMap.values());
}
